import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Droplets, Calendar, Clock, MapPin, Navigation, MessageCircle, Share2,
  ArrowLeft, Crown, Home as HomeIcon, Users, Loader2, Camera,
} from 'lucide-react';

import Button from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import ShareSheet from '../components/features/ShareSheet';
import PageLayout from '../components/layout/PageLayout';
import { api } from '../services/api';
import { mapsDirectionsUrl, whatsappUrl, fullAddress } from '../lib/utils';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],
      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/contato'],
      ['Área Administrativa', '/admin'],
    ],
  },
];


function TeamMember({ icon: Icon, role, tone, name, phone, avatars }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex -space-x-2">
        {avatars.map((a, i) => <Avatar key={i} src={a} alt={name} size={40} />)}
      </div>
      <div className="min-w-0 flex-1">
        <Badge tone={tone} className="mb-0.5"><Icon size={12} /> {role}</Badge>
        <p className="truncate text-sm font-semibold text-ink">{name}</p>
      </div>
      <a
        href={whatsappUrl(phone)}
        target="_blank"
        rel="noreferrer"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]/10 text-[#1ebe57] transition-transform duration-200 hover:-translate-y-0.5"
        aria-label={`WhatsApp de ${name}`}
      >
        <MessageCircle size={16} />
      </a>
    </div>
  );
}

export default function CellShare() {
  const { id } = useParams();
  const [cell, setCell] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    Promise.all([api.getCells(), api.getNetworks()]).then(([cells, networks]) => {
      const c = cells.find((x) => x.id === id);
      setCell(c || null);
      setNetwork(c ? networks.find((n) => n.id === c.networkId) : null);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 size={26} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!cell) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <MapPin size={30} className="text-ink-faint" />
        <p className="mt-3 font-heading text-lg font-bold text-ink">Célula não encontrada</p>
        <Link to="/celulas" className="mt-4">
          <Button data-testid="share-notfound-cta">Ver todas as células</Button>
        </Link>
      </div>
    );
  }

  const leaderAvatars = cell.leader.avatar2 ? [cell.leader.avatar, cell.leader.avatar2] : [cell.leader.avatar];

  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <div data-testid="cell-share-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary to-primary-dark px-5 pb-8 pt-5 text-white md:px-8">

        <div className="app-container !px-0">
          <div className="flex items-center justify-between">
            <Link to="/celulas" className="flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white" data-testid="share-back">
              <ArrowLeft size={16} /> Células
            </Link>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-white/90">
              <Droplets size={16} /> Águas Vivas
            </div>
          </div>
          <div className="mt-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent-light">Você está convidado(a)</span>
            <h1 className="mt-1 font-heading text-3xl font-extrabold leading-tight md:text-4xl">{cell.name}</h1>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold backdrop-blur">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: network?.color || '#f59e0b' }} />
              {network?.name}
            </span>
          </div>
        </div>
      </div>

      <div className="app-container py-5">
        {/* Quando & Onde */}
        <div className="grid grid-cols-2 gap-3 md:max-w-2xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
            <Calendar size={18} className="text-primary" />
            <p className="mt-2 font-heading font-bold text-ink">{cell.day}</p>
            <p className="flex items-center gap-1 text-sm text-ink-soft"><Clock size={13} /> {cell.time}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
            <MapPin size={18} className="text-accent-dark" />
            <p className="mt-2 text-sm font-semibold leading-snug text-ink">{cell.address.neighborhood}</p>
            <p className="text-xs text-ink-soft">{cell.address.city}</p>
          </div>
        </div>

        <p className="mt-3 flex items-start gap-1.5 rounded-xl bg-slate-50 p-3 text-sm text-ink-soft md:max-w-2xl">
          <MapPin size={15} className="mt-0.5 shrink-0 text-ink-faint" /> {fullAddress(cell.address)}
        </p>

        {/* Equipe */}
        <h2 className="mt-6 font-heading text-lg font-bold text-ink md:text-xl">Quem te recebe</h2>
        <div className="mt-3 grid gap-2.5 md:max-w-2xl md:grid-cols-2">
          <TeamMember icon={Crown} role="Líder" tone="primary" name={cell.leader.name} phone={cell.leader.phone} avatars={leaderAvatars} />
          <TeamMember icon={HomeIcon} role="Anfitrião" tone="accent" name={cell.host.name} phone={cell.host.phone} avatars={[cell.host.avatar]} />
          {network?.supervisor && (
            <TeamMember icon={Users} role="Supervisor" tone="neutral" name={network.supervisor.name} phone={network.supervisor.phone} avatars={network.supervisor.avatar2 ? [network.supervisor.avatar, network.supervisor.avatar2] : [network.supervisor.avatar]} />
          )}
        </div>

        {/* CTAs */}
        <div className="mt-6 space-y-2.5 md:max-w-2xl">
          <div className="flex gap-2.5">
            <Button as="a" href={mapsDirectionsUrl(cell.address)} target="_blank" rel="noreferrer" variant="outline" className="flex-1" data-testid="share-directions">
              <Navigation size={17} /> Como Chegar
            </Button>
            <Button as="a" href={whatsappUrl(cell.leader.phone, `Olá! Vi o convite da ${cell.name} e gostaria de participar.`)} target="_blank" rel="noreferrer" variant="whatsapp" className="flex-1" data-testid="share-leader-whatsapp">
              <MessageCircle size={17} /> Quero ir
            </Button>
          </div>
          <Button onClick={() => setShowShare(true)} variant="accent" size="lg" className="w-full" data-testid="share-invite-btn">
            <Share2 size={18} /> Compartilhar convite
          </Button>
          <Link to={`/celula/${cell.id}/fotos`} className="block">
            <Button variant="outline" size="lg" className="w-full" data-testid="share-photos-btn">
              <Camera size={18} /> Ver fotos da célula
            </Button>
          </Link>
        </div>
      </div>



      {showShare && <ShareSheet cell={cell} network={network} onClose={() => setShowShare(false)} />}
      </div>
    </PageLayout>
  );
}


